<!-- eslint-disable max-lines -->
<template>
  <section class="container">
    <div
      v-if="isLoading"
      class="d-flex justify-content-center align-items-center"
      style="min-height: 400px;"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>
    <div v-else-if="isPaid">
      <div class="row mb-2 mb-md-5">
        <div class="h3 text-center mb-1">
          Votre transaction a bien été payée, vous n'avez plus rien à faire
        </div>
      </div>
    </div>
    <div v-else>
      <div class="text-center h3 mb-3 fw-normal">
        <template v-if="contractHeaderInfo && contractHeaderInfo.productName">
          Détails de la transaction du contrat
          <span class="fw-bold">{{ contractHeaderInfo.productName }}</span>
          n°{{ contractHeaderInfo.contractReference }}
        </template>
        <template v-else>
          Détails du paiement pour la transaction
          <strong>#{{ transaction.reference }}</strong>
        </template>
      </div>
      <div
        v-if="!contractHeaderInfo || !contractHeaderInfo.productName"
        class="text-center fw-normal mb-3"
      >
        pour le contrat #{{ transaction.payments[0]?.contractReference }}
      </div>

      <div
        v-if="transactionHavePayments"
        class="col-12 col-lg-9 mx-auto"
      >
        <div class="card border-0 shadow mb-2">
          <div
            class="card-body px-md-4 py-2 d-flex flex-column"
            :class="color"
          >
            <div
              v-if="viewMode === 'ADD_NEW' || hasBothPaymentTypes"
              class="d-flex flex-row mx-auto bg-blue-300 fw-bold border-radius-20 mb-3"
              style="padding: 4px"
            >
              <div
                class="d-flex btn-switch"
                :class="{ 'bg-blue-600 text-white' : isCreditCard }"
                @click="isCreditCard = true"
              >
                CB
              </div>
              <div
                class="d-flex btn-switch ms-1"
                :class="{ 'bg-blue-600 text-white' : !isCreditCard }"
                @click="isCreditCard = false"
              >
                IBAN
              </div>
            </div>
            <div
              v-if="viewMode === 'USE_EXISTING'"
              class="row"
            >
              <div class="col-12 d-flex flex-md-row flex-col justify-content-between align-items-center mb-3">
                <div>
                  <h4 class="my-2">
                    Moyens de paiement
                  </h4>
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="viewMode = 'ADD_NEW'"
                  >
                    Ajouter {{ isCreditCard ? 'une carte bancaire' : 'un compte bancaire' }}
                  </button>
                </div>
              </div>
              <div class="col-12">
                <transition-group name="flip-list">
                  <div
                    v-for="paymentMethod in filteredPaymentMethods"
                    :key="paymentMethod.id"
                    class="mb-3 hover-primary bg-white"
                    :class="{ 'border-primary' : selectedPaymentMethod?.id === paymentMethod.id }"
                    @click="setSelectedPaymentMethod(paymentMethod)"
                  >
                    <payment-method-display
                      v-if="paymentMethod.type !== 'bank_account'"
                      :payment-method="paymentMethod"
                      :price="price"
                      :stripe="stripe"
                    >
                      <span
                        v-if="paymentMethod.id === selectedPaymentMethod.id"
                        class="badge badge-pill rounded-pill px-2 fs-6 fw-bold lead bg-blue-300"
                      >
                        Sélectionné
                      </span>
                      <span v-else>&nbsp;</span>
                    </payment-method-display>
                    <div
                      v-else
                      class="card"
                      :class="{ 'border-primary' : selectedPaymentMethod?.id === paymentMethod.id }"
                      @click="setSelectedPaymentMethod(paymentMethod)"
                    >
                      <div class="card-body">
                        <div class="d-flex flex-row justify-content-between">
                          <div>
                            <div class="mb-1 fw-bold">
                              Compte bancaire - SEPA
                            </div>
                            <small class="text-muted">{{ paymentMethod.name }}</small>&nbsp;
                            <small class="text-muted">{{ paymentMethod.countryCode }}</small>&nbsp;*** **** **** **
                            <small class="text-muted">{{ paymentMethod.numberEnding }}</small>
                          </div>
                          <div>
                            <span
                              v-if="paymentMethod.id === selectedPaymentMethod.id"
                              class="badge badge-pill rounded-pill px-2 fs-6 fw-bold lead bg-blue-300"
                            >
                              Sélectionné
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition-group>
              </div>
              <div class="col-12 text-center d-flex flex-column gap-2">
                <button
                  type="button"
                  class="btn btn-primary w-50 mx-auto"
                  :disabled="isPayDisabled"
                  @click="viewMode = 'ADD_NEW'"
                >
                  Payer {{ formattedPrice }} €
                </button>
              </div>
            </div>

            <div
              v-else-if="viewMode === 'SELECT_FROM_ALL'"
              class="row"
            >
              <div class="col-12">
                <div
                  v-for="paymentMethod in filteredPaymentMethods"
                  :key="paymentMethod.id"
                  class="mb-3"
                >
                  <payment-method-display
                    v-if="paymentMethod.type !== 'bank_account'"
                    :payment-method="paymentMethod"
                    :price="price"
                    :stripe="stripe"
                  />
                  <div
                    v-else
                  >
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                          <h6 class="mb-1">
                            Compte bancaire SEPA
                          </h6>
                          <small class="text-muted">{{ paymentMethod.iban ?? `${paymentMethod.countryCode} **** **** *** *** ${paymentMethod.numberEnding}` }}</small>
                        </div>
                        <div>
                          <v-loader
                            :next="() => payWithOtherBankAccount(paymentMethod)"
                            :title="`Payer ${formattedPrice} €`"
                            class="btn btn-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="row"
            >
              <div class="col-12 mb-1 justify-content-between d-flex flex-md-row flex-col align-items-center">
                <div class="h5 mb-1">
                  Régler par {{ isCreditCard ? 'carte bancaire' : 'prélèvement SEPA' }}
                </div>
                <div
                  v-if="havePaymentMethod"
                  class=""
                >
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="viewMode = 'USE_EXISTING'"
                  >
                    Retour
                  </button>
                </div>
              </div>
              <div
                v-show="isCreditCard"
                class="col-12 mb-4"
              >
                <v-credit-card
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
              <div
                v-show="!isCreditCard && !useGoCardless"
                class="col-12 mb-4"
              >
                <v-stripe-iban
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
              <div
                v-show="!isCreditCard && useGoCardless"
                class="col-12 mb-4"
              >
                <v-gocardless-iban
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
            </div>
            <div class="text-center mt-3">
              <button
                type="button"
                class="btn btn-link text-muted text-decoration-none p-0"
                style="font-size: 0.9rem;"
                @click="() => showDetails = !showDetails"
              >
                <span>{{ showDetails ? 'Masquer le détail' : 'Voir le détail' }}</span>
                <v-icon
                  class="ms-1"
                  icon="chevron-gray"
                  width="10"
                  height="10"
                  :class="showDetails ? 'transition-icon-up': 'transition-icon-down'"
                />
              </button>
            </div>
            <transition name="slide-fade">
              <div
                v-if="showDetails"
                class="details-container mt-4"
              >
                <!-- Summary Header -->
                <div class="card border-0 bg-light mb-3">
                  <div class="card-body py-2 px-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="fw-bold text-secondary">Montant total</span>
                      <span class="h5 mb-0 fw-bold text-primary">{{ formattedPrice }} €</span>
                    </div>
                  </div>
                </div>

                <!-- Contract Details -->
                <div
                  v-for="(contractRef, index) in contractReferences"
                  :key="index"
                  class="mb-4"
                >
                  <!-- Contract Header -->
                  <div class="d-flex align-items-center mb-3">
                    <div
                      class="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-2"
                      style="width: 32px; height: 32px;"
                    >
                      <font-awesome-icon
                        icon="file-contract"
                        class="text-primary"
                        style="font-size: 14px;"
                      />
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <div class="fw-bold text-dark">
                          Contrat {{ contractRef }}
                        </div>
                        <span
                          v-if="getProductForContract(contractRef)"
                          class="badge bg-primary text-white px-2 py-1 fw-normal rounded-pill"
                          style="font-size: 0.75rem;"
                        >
                          {{ getProductForContract(contractRef).name }}
                        </span>
                      </div>
                      <small class="text-muted">{{ contractPayments(contractRef).length }} période(s) de paiement</small>
                    </div>
                  </div>

                  <!-- Payment Periods -->
                  <div class="ms-4 ps-2 border-start border-2 border-primary border-opacity-25">
                    <div
                      v-for="(p, innerIndex) in contractPayments(contractRef)"
                      :key="innerIndex"
                      class="mb-2 position-relative"
                    >
                      <!-- Timeline Dot -->
                      <div
                        class="position-absolute bg-primary rounded-circle"
                        style="width: 8px; height: 8px; left: -21px; top:calc(50% - 4px);"
                      />

                      <!-- Payment Card -->
                      <div class="card border-0 shadow-sm hover-shadow-md transition-shadow">
                        <div class="card-body px-3 py-1">
                          <div class="row align-items-center">
                            <div class="col-md-8">
                              <div class="d-flex align-items-center flex-wrap">
                                <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                                  <font-awesome-icon
                                    icon="calendar-alt"
                                    class="text-muted me-2"
                                    style="font-size: 14px;"
                                  />
                                  <span class="fw-medium">{{ toDateFrNumeric(p.startDate) }}</span>
                                </div>
                                <font-awesome-icon
                                  icon="long-arrow-alt-right"
                                  class="text-muted mx-2 mb-2 mb-md-0"
                                  style="font-size: 14px;"
                                />
                                <div class="d-flex align-items-center mb-2 mb-md-0">
                                  <font-awesome-icon
                                    icon="calendar-check"
                                    class="text-muted me-2"
                                    style="font-size: 14px;"
                                  />
                                  <span class="fw-medium">{{ toDateFrNumeric(p.endDate) }}</span>
                                </div>
                              </div>
                            </div>
                            <div class="col-md-4 text-md-end">
                              <div class="d-inline-flex align-items-center bg-light rounded-pill px-3 py-2">
                                <span
                                  class="fw-bold text-dark"
                                  style="font-size: 1.1rem;"
                                >{{ p.totalToPay | formatPrice(true) }} €</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row mb-2 mb-md-5">
          <div class="h3 text-center mb-1">
            Cette transaction n'est plus disponible
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>

/* eslint-disable max-lines */
import { mapGetters } from 'vuex';
import { transform, contracts as contractsMixin } from '@/mixins';
import { toDateFrNumeric } from '@/utils/_Date';

import CreditCard from '@/components/checkout/stripe/CreditCard.vue';
import StripeIban from '@/components/checkout/stripe/StripeIban.vue';
import GoCardlessIban from '@/components/checkout/gocardless/GoCardlessIban.vue';
import Loader from '@/components/common/helpers/Loader.vue';
import PaymentMethodDisplay from '@/components/checkout/stripe/PaymentMethodDisplay.vue';

export default {
  name: 'TransactionView',
  components: {
    'v-stripe-iban': StripeIban,
    'v-credit-card': CreditCard,
    'v-gocardless-iban': GoCardlessIban,
    'v-loader': Loader,
    'payment-method-display': PaymentMethodDisplay,
  },
  mixins: [transform, contractsMixin],
  emits: ['update:progress'],
  data() {
    return {
      transaction: {},
      error: null,
      showDetails: false,
      isCreditCard: true,
      paymentMethodSaved: null,
      selectedPaymentMethod: null,
      viewMode: 'USE_EXISTING',
      transactionHavePayments: true,
      isLoading: true,
      toDateFrNumeric,
    };
  },
  computed: {
    ...mapGetters({
      paymentMethodsShadow: 'payments/paymentMethods',
      goCardlessBankAccounts: 'payments/goCardlessBankAccounts',
      order: 'orders/get'
    }),
    color() {
      const color = this.isCreditCard ? 'yellow' : 'green';
      return `box-shadow-${color}-300`;
    },
    contractHeaderInfo() {
      const contractRef = this.transaction.payments?.[0]?.contractReference;
      if (!contractRef) return null;
      const product = this.getProductForContract(contractRef);
      return {
        contractReference: contractRef,
        productName: product?.name || null
      };
    },
    price() {
      return this.transaction.totalToPay;
    },
    formattedPrice() {
      return parseFloat(this.price || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
    },
    isPaid() {
      if (this.transaction.status === 'PAID' || this.transaction.status === 'IN_PROGRESS') {
        return true;
      }
      return false;
    },
    contractReferences() {
      const references = new Set();
      this.payments.forEach((p) => references.add(p.contractReference));
      return Array.from(references);
    },
    paymentMethods() {
      return this.paymentMethodsShadow
        .filter(({ active }) => active);
    },
    allPaymentMethods() {
      const stripeMethods = this.paymentMethodsShadow
        .filter(({ type }) => type !== 'bank_account');
      const bankAccountsArray = Array.isArray(this.goCardlessBankAccounts)
        ? this.goCardlessBankAccounts
        : [];

      const bankAccounts = bankAccountsArray
        .map((account, index) => ({
          ...account,
          id: account.id || `bank_account_${index}`,
          type: 'bank_account',
          provider: 'gocardless',
        }));

      return [...stripeMethods, ...bankAccounts];
    },
    havePaymentMethod() {
      return this.allPaymentMethods.length > 0;
    },
    activePaymentMethod() {
      // Find active Stripe method first
      const activeStripeMethod = this.paymentMethodsShadow.find(({ active }) => active);
      if (activeStripeMethod) {
        return activeStripeMethod;
      }

      // Then check for active GoCardless bank account
      const bankAccountsArray = Array.isArray(this.goCardlessBankAccounts)
        ? this.goCardlessBankAccounts
        : [];
      const activeGoCardlessAccount = bankAccountsArray.find(({ active }) => active);
      if (activeGoCardlessAccount) {
        return {
          ...activeGoCardlessAccount,
          type: 'bank_account',
          provider: 'gocardless',
        };
      }

      return null;
    },
    hasActivePaymentMethod() {
      return this.activePaymentMethod !== null;
    },
    filteredPaymentMethods() {
      if (!this.havePaymentMethod) return [];

      return this.allPaymentMethods.filter((method) => {
        if (this.isCreditCard) {
          return method.type === 'card' || method.type === 'CARD';
        }
        return method.type === 'bank_account' || method.type === 'sepa_debit';
      }).toSorted((a, b) => {
        if (a.id === this.selectedPaymentMethod?.id) return -1;
        if (b.id === this.selectedPaymentMethod?.id) return 1;
        return 0;
      });
    },
    hasBothPaymentTypes() {
      if (!this.havePaymentMethod) return false;

      const hasCard = this.allPaymentMethods.some((method) =>
        method.type === 'card' || method.type === 'CARD'
      );
      const hasIban = this.allPaymentMethods.some((method) =>
        method.type === 'bank_account' || method.type === 'sepa_debit'
      );

      return hasCard && hasIban;
    },
    isPayDisabled() {
      if (!this.filteredPaymentMethods || this.filteredPaymentMethods.length === 0) {
        return true;
      }

      if (!this.selectedPaymentMethod || !this.selectedPaymentMethod.id) {
        return true;
      }

      const existsInCurrentList = this.filteredPaymentMethods.some(
        (m) => m.id === this.selectedPaymentMethod.id
      );
      return !existsInCurrentList;
    },
  },
  watch: {
    activePaymentMethod: {
      immediate: true,
      handler(newMethod) {
        this.selectedPaymentMethod = newMethod;
      },
    },
  },
  created() {
    this.stripe = window.Stripe(import.meta.env.VITE_STRIPE_KEY);
  },
  mounted() {
    this.$store.dispatch('analytics/checkout', { step: 1, option: 'Stripe' });
  },
  async beforeMount() {
    // Load contracts first to determine payment provider
    await this.ensureContractsLoaded();

    // Load both Stripe payment methods and GoCardless bank accounts
    Promise.allSettled([
      this.$store.dispatch('payments/getPaymentMethods'),
      this.$store.dispatch('payments/getGoCardlessBankAccounts')
    ]).then(([stripeResult, goCardlessResult]) => {
      if (stripeResult.status === 'fulfilled' && stripeResult.value) {
        this.paymentMethodSaved = stripeResult.value.find((method) => method.active);
        this.isCreditCard = this.paymentMethodSaved
          ? (this.paymentMethodSaved.type === 'card' || this.paymentMethodSaved.type === 'CARD')
          : false;
      }

      if (!this.paymentMethodSaved && this.havePaymentMethod) {
        this.paymentMethodSaved = this.allPaymentMethods[0];
      }

      if (!this.havePaymentMethod) {
        this.viewMode = 'ADD_NEW';
      }

      const hasCard = this.allPaymentMethods.some((method) =>
        method.type === 'card' || method.type === 'CARD'
      );
      const hasIban = this.allPaymentMethods.some((method) =>
        method.type === 'bank_account' || method.type === 'sepa_debit'
      );

      if (!hasCard && hasIban) {
        this.isCreditCard = false;
      } else if (hasCard && !hasIban) {
        this.isCreditCard = true;
      }
    }).catch(() => this.$router.replace('/failure/order_checkout'));

    const { id } = this.$route.params;

    if (!id || id === 'null' || id === null || id === undefined) {
      console.error('Invalid transaction ID in route params:', id);
      this.$router.replace('/failure/order_checkout');
      return;
    }

    this.$store.dispatch('checkout/getTransactionFromId', id)
      .then((transaction) => {
        this.transaction = transaction;
        this.transactionHavePayments = transaction.payments !== undefined;
        this.payments = transaction.payments;

        this.$store.commit('checkout/SET_CHECKOUT', transaction);
        this.isLoading = false;
      })
      .catch((error) => {
        console.error('Failed to load transaction:', error);
        this.isLoading = false;
        this.$router.replace('/failure/order_checkout');
      });
  },
  methods: {
    setSelectedPaymentMethod(method) {
      this.selectedPaymentMethod = method;
      if (method.type === 'CARD' || method.type === 'card')
        this.$store.dispatch('payments/activatePaymentMethodFromId', method.id);
      else
        this.$store.dispatch('payments/activateGoCardlessBankAccount', method.id);
    },
    // emit event to the parent when payment is successful
    next() {
      this.$emit('update:progress');
      return Promise.resolve();
    },
    contractPayments(contractReference) {
      return this.payments
        .filter((p) => p.contractReference === contractReference)
        .map((p) => p);
    },
    getProductForContract(contractReference) {
      const orders = this.$store.getters['orders/get'];
      if (!orders || !Array.isArray(orders) || orders.length === 0) {
        return null;
      }

      for (const order of orders) {
        if (order.subscriptions && Array.isArray(order.subscriptions)) {
          const subscription = order.subscriptions.find(
            (sub) => sub.reference === contractReference
          );

          if (subscription && subscription.product) {
            return subscription.product;
          }
        }
      }

      return null;
    },
    goToAddPaymentMethod() {
      this.$router.push('/client/account/payments');
    },
    async payWithOtherBankAccount(paymentMethod) {
      await this.$store.dispatch('payments/activateGoCardlessBankAccount', paymentMethod.id);
      await this.payWithBankAccount();
    },
    async payWithBankAccount() {
      try {
        const transactionId = this.transaction.id;

        if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
          console.error('Invalid transactionId:', transactionId);
          throw new Error(`Invalid transactionId in Transaction.vue: ${transactionId}`);
        }

        await this.$store.dispatch('checkout/paymentGoCardless', {
          transactionId,
          iban: null, // IBAN not available from existing accounts
        });

        this.$router.push('/client/confirmation');
      } catch (error) {
        console.error('Payment error:', error);
        // Handle error appropriately
      }
    },
  },
};
</script>
<style scoped>
.transition-icon-up {
  transform: rotate(-90deg);
}
.transition-icon-down {
  transform: rotate(90deg);
}
.card-body {
  border-radius: 15px;
  transition: box-shadow .5s, height .5s;
}
.btn-switch,
.border-radius-20 {
  border-radius: 20px;
}
.btn-switch {
  padding: 4px 8px;
  transition: backgroundColor 1s;
  background-color: var(--bs-blue-300);
  color: var(--bs-blue-700);
  cursor:pointer;
}
.btn-switch:hover:not(.active) {
  background-color: var(--bs-blue-400);
  color: white;
}
.btn-switch.active {
  background-color: var(--bs-blue-700);
  color: white;
}
.btn-link:focus,
.btn-link:focus-visible {
  outline: none;
  box-shadow: none;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter,
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
.details-container {
  overflow: hidden;
}
.hover-primary {
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  cursor: pointer;
}
.hover-primary:hover {
  border: 2px solid var(--bs-primary);
}
.flip-list-move {
  transition: transform 1s;
}
.hover-shadow-md {
  transition: box-shadow 0.3s ease, transform 0.2s ease;
}
.hover-shadow-md:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}
.transition-shadow {
  transition: all 0.3s ease;
}
</style>
